import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  FormControl,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { CustomAutocomplete } from '../../../../../../../../../Infra/CustomAutocomplete/CustomAutocomplete';
import { LabelAutocompleteInput } from '../../../../../../../../../Infra/LabelAutocompleteInput/LabelAutocompleteInput';
import { useRuleRow } from './useRuleRow';
import { RuleRowProps } from './types';
import { RuleCondition, SecurityType } from '../../../../../../../types';
import ConfirmationModal from '../../../../../../../../../Modal/ConfirmationModal';
import './RuleRow.scss';

export const RuleRow = (props: RuleRowProps) => {
  const { rule, ruleNumber, availableHeaders, onRemove, isFirstRule } = props;

  const {
    isExpanded,
    toggleExpanded,
    confirmDialogOpen,
    handleAddLabel,
    handleColumnSourceChange,
    addMenuAnchor,
    handleOpenAddMenu,
    handleCloseAddMenu,
    handleAddFileColumn,
    handleAddSecurityTypeColumn,
    handleRemoveColumn,
    confirmRemoveColumn,
    cancelRemoveColumn,
    handleConditionChange,
    handleRemoveLabel,
    handleSecurityTypeChange,
  } = useRuleRow(props);

  return (
    <>
      <Accordion
        expanded={isExpanded}
        onChange={toggleExpanded}
        className="ruleRow"
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className="ruleHeader"
        >
          <Box className="headerContent">
            <Typography variant="subtitle2" className="ruleTitle">
              RULE {ruleNumber}
            </Typography>
            {!isFirstRule && (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="deleteButton"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails className="ruleContent">
          <Box className="ruleColumns">
            {rule.columns.map((column, index) => (
              <React.Fragment key={column.id}>
                {column.columnType === 'SECURITY_TYPE' ? (
                  /* Security Type column: a simple select for OTC/Tradable */
                  <Box className="column">
                    <Box className="columnHeader">
                      <Typography variant="caption" className="columnLabel">
                        SECURITY TYPE
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveColumn(column.id)}
                        className="deleteColumnButton"
                        disabled={rule.columns.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={column.securityType || 'OTC'}
                        onChange={e => handleSecurityTypeChange(column.id, e.target.value as SecurityType)}
                      >
                        <MenuItem value="OTC">OTC</MenuItem>
                        <MenuItem value="Tradable">Tradable</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  /* FILE_COLUMN: existing rendering */
                  <Box className="column">
                    <Box className="columnHeader">
                      <Typography variant="caption" className="columnLabel">
                        COLUMN {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveColumn(column.id)}
                        className="deleteColumnButton"
                        disabled={rule.columns.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <CustomAutocomplete
                      value={column.sourceColumn || null}
                      onChange={(_, newValue) =>
                        handleColumnSourceChange(column.id, newValue || '')
                      }
                      options={availableHeaders}
                      fullWidth
                      className="columnSelect"
                      placeholder="Select column..."
                    />

                    <LabelAutocompleteInput
                      showCheckboxes
                      selectedColumn={column.sourceColumn}
                      labels={column.labels}
                      onAdd={value => handleAddLabel(column.id, value)}
                      onRemove={labelId => handleRemoveLabel(column.id, labelId)}
                      placeholder="Select or type label values..."
                      disabled={
                        !column.sourceColumn || column.sourceColumn.trim() === ''
                      }
                    />
                  </Box>
                )}

                {/* Condition selector between columns (not after last column) */}
                {index < rule.columns.length - 1 && (
                  <Box className="column conditionColumn">
                    <Box className="columnHeader">
                      <Typography variant="caption" className="columnLabel">
                        CONDITION
                      </Typography>
                    </Box>
                    <FormControl size="small" className="conditionSelect">
                      <Select
                        value={rule.conditions[index] || RuleCondition.OR}
                        onChange={e =>
                          handleConditionChange(
                            index,
                            e.target.value as RuleCondition
                          )
                        }
                      >
                        <MenuItem value={RuleCondition.OR}>OR</MenuItem>
                        <MenuItem value={RuleCondition.AND}>AND</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </React.Fragment>
            ))}

            {/* Add Condition Button + Menu */}
            <Box className="addColumnWrapper">
              <Box className="headerSpacer" />
              <Button
                startIcon={<AddIcon />}
                onClick={handleOpenAddMenu}
                variant="outlined"
                className="addColumnButton"
                size="small"
              >
                Add Condition
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={handleCloseAddMenu}
              >
                <MenuItem onClick={handleAddFileColumn}>File Column</MenuItem>
                <MenuItem onClick={handleAddSecurityTypeColumn}>Security Type</MenuItem>
              </Menu>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={confirmDialogOpen}
        title="Delete Column?"
        message="Are you sure you want to delete this column?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmRemoveColumn}
        onCancel={cancelRemoveColumn}
        severity="error"
      />
    </>
  );
};
